import { TObject } from "@sinclair/typebox";
import { Lang } from "./i18n";
import { mergeDeep } from "./object";
import { checkSettingsSchema } from "./schema";

export interface VisArgs {
    csrfToken: string;
    apiToken: string;
    apiBaseUrl: string;
    staticServerBaseUrl: string;
    orgId: number;
    visId: number;
    netId?: number;
    deviceId?: number;
    language: Lang;
    userCanEditOrgObjects: boolean;
    baseVisUrl: string;
    dataAttrs: Record<string, string>;
    ocpBaseUrl: string;
    settings?: object;
}

let currentScript: HTMLScriptElement;
let visView: HTMLDivElement;
let container: HTMLDivElement;
let visArgs: VisArgs;

export function init(
    defaultSettings: object,
    run: () => void,
    clearContainerStyles = true,
    addLinkToCss = true,
    schema?: TObject,
): void {
    currentScript =
        (document.currentScript as HTMLScriptElement | undefined) ??
        document.querySelector(".vis-view script")!;

    const urlParts = currentScript.src.split("/");
    const baseVisUrl = urlParts.slice(0, -1).join("/");
    if (addLinkToCss) {
        const name = urlParts[urlParts.length - 1].split(".")[0];
        const head = document.getElementsByTagName("head")[0];
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.type = "text/css";
        link.href = `${baseVisUrl}/${name}.css`;
        link.media = "all";
        head.appendChild(link);
    }

    window.addEventListener("load", () => {
        visView = currentScript.parentElement! as HTMLDivElement;
        visArgs = readVisArgs(
            currentScript,
            visView.parentElement!,
            baseVisUrl,
            defaultSettings,
            schema,
        );
        container = document.createElement("div");
        if (clearContainerStyles) {
            container.setAttribute("style", "all: initial;");
        }
        visView.append(container);
        // eslint-disable-next-line no-console
        run();
    });
}

export function getCurrentScript(): HTMLOrSVGScriptElement {
    return currentScript;
}

export function getContainer(): HTMLDivElement {
    return container;
}

export function getVisView(): HTMLDivElement {
    return visView;
}

export function getVisArgs<T>(): VisArgs & { settings: T } {
    return visArgs as VisArgs & { settings: T };
}

export function getOcpBaseUrl(): string {
    return visArgs.ocpBaseUrl;
}

export function getLanguage(): Lang {
    return visArgs.language;
}

export function getBaseVisUrl(): string {
    return visArgs.baseVisUrl;
}

export function getApiToken(): string {
    return visArgs.apiToken;
}

export function getApiBaseUrl(): string {
    return visArgs.apiBaseUrl;
}

function readVisArgs(
    script: HTMLScriptElement,
    vis: Element,
    baseVisUrl: string,
    defaultSettings: object,
    schema?: TObject,
): VisArgs {
    const dataAttrs: Record<string, string> = {};
    for (const attr of Array.from(script.attributes)) {
        if (attr.nodeName.startsWith("data-")) {
            dataAttrs[attr.nodeName.slice(5)] = attr.value;
        }
    }
    const baseOcpUrl = window.location.href.split("/").slice(0, 3).join("/");

    let scriptSettings: object = {};
    const scriptContent = script.innerHTML.trim();
    if (scriptContent.trim().startsWith("{")) {
        scriptSettings = JSON.parse(scriptContent) as object;
    }
    if (script.nextElementSibling && script.nextElementSibling.nodeName === "SCRIPT") {
        const body = "return " + (script.nextElementSibling as HTMLScriptElement).innerHTML.trim();
        // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
        scriptSettings = new Function(body)() as object;
    }
    let settings = mergeDeep(defaultSettings, scriptSettings) as object;
    if (schema) {
        settings = checkSettingsSchema(settings, schema);
    }

    return {
        csrfToken: vis.getAttribute("data-csrfToken")!,
        apiToken: vis.getAttribute("apiToken")!,
        apiBaseUrl: vis.getAttribute("apiBaseUrl")!,
        staticServerBaseUrl: vis.getAttribute("staticServerBaseUrl")!,
        orgId: Number(vis.getAttribute("orgId")!),
        visId: Number(vis.getAttribute("visId")!),
        netId: vis.getAttribute("netId") ? Number(vis.getAttribute("netId")) : undefined,
        deviceId: vis.getAttribute("deviceId") ? Number(vis.getAttribute("deviceId")) : undefined,
        language: vis.getAttribute("language") as Lang,
        userCanEditOrgObjects: vis.getAttribute("data-canEditOrgObjects") === "true",
        baseVisUrl,
        dataAttrs,
        ocpBaseUrl: baseOcpUrl,
        settings,
    };
}

export function getVisAliases(): Record<string, string> {
    const settings = getVisArgs().settings;
    if (!settings) {
        return {};
    }
    if ("aliases" in settings) {
        return (settings as { aliases: Record<string, string> }).aliases;
    }
    return {};
}
